import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react'

function visibleArea(): CSSProperties | undefined {
  const viewport = window.visualViewport ?? null
  return viewport === null
    ? undefined
    : { left: viewport.offsetLeft, top: viewport.offsetTop, width: viewport.width, height: viewport.height }
}

/**
 * La partie de la page reellement visible. Le clavier d'un telephone ne reduit
 * pas la page, il la recouvre : une fenetre calee sur la page entiere passe a
 * moitie dessous, et son contenu, qui « tient », ne defile plus.
 */
function useVisibleArea() {
  const [area, setArea] = useState(visibleArea)

  useEffect(() => {
    const viewport = window.visualViewport ?? null
    if (viewport === null) {
      return
    }

    const update = () => setArea(visibleArea())
    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
    }
  }, [])

  return area
}

function scrollsUnder(target: EventTarget | null, boundary: HTMLElement) {
  for (
    let element = target instanceof Element ? target : null;
    element !== null && element !== boundary;
    element = element.parentElement
  ) {
    const style = getComputedStyle(element)
    if (
      (/auto|scroll/.test(style.overflowY) && element.scrollHeight > element.clientHeight) ||
      (/auto|scroll/.test(style.overflowX) && element.scrollWidth > element.clientWidth)
    ) {
      return true
    }
  }

  return false
}

/**
 * Tant qu'une fenetre est ouverte, la page derriere ne defile pas.
 *
 * `overflow: hidden` ne suffit pas sur un iPhone : clavier ouvert, un doigt
 * pose sur ce qui ne defile pas (le fond, l'en-tete, un contenu qui tient)
 * fait glisser toute la page. Ce geste-la est refuse, sauf a deux doigts
 * (le zoom).
 */
function useBackgroundLock(overlay: { current: HTMLElement | null }) {
  useEffect(() => {
    const root = document.documentElement
    const { overflow, paddingRight } = root.style
    const scrollbar = window.innerWidth - root.clientWidth
    root.style.overflow = 'hidden'
    // La barre de defilement disparait : sans cela la page se decale d'autant.
    if (scrollbar > 0) {
      root.style.paddingRight = `${scrollbar}px`
    }

    const element = overlay.current
    const onTouchMove = (event: TouchEvent) => {
      if (element !== null && event.touches.length === 1 && !scrollsUnder(event.target, element)) {
        event.preventDefault()
      }
    }
    element?.addEventListener('touchmove', onTouchMove, { passive: false })

    return () => {
      element?.removeEventListener('touchmove', onTouchMove)
      root.style.overflow = overflow
      root.style.paddingRight = paddingRight
    }
  }, [overlay])
}

/**
 * Fenetre en trois parties : un en-tete (titre et croix) et un pied
 * (`footer`, les actions) qui ne bougent pas, et entre les deux le contenu,
 * seul a defiler. Elle occupe la partie visible de la page, au-dessus du
 * clavier d'un telephone : la croix et le bouton d'enregistrement restent
 * ainsi a portee.
 */
export default function Modal({
  title,
  onClose,
  footer,
  children,
}: {
  title: string
  onClose: () => void
  footer?: ReactNode
  children: ReactNode
}) {
  const titleId = useId()
  const overlay = useRef<HTMLDivElement>(null)
  const area = useVisibleArea()
  useBackgroundLock(overlay)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div
      ref={overlay}
      style={area}
      className="fixed inset-0 z-20 flex items-center justify-center bg-neutral-950/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-xl dark:border-neutral-800 dark:bg-neutral-900"
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-neutral-200 px-6 py-4 dark:border-neutral-800">
          <h2 id={titleId} className="min-w-0 pt-0.5 text-lg font-semibold tracking-tight break-words text-neutral-900 dark:text-neutral-50">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="-mr-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5">{children}</div>
        {footer !== undefined && (
          <div className="shrink-0 border-t border-neutral-200 px-6 py-3 dark:border-neutral-800">{footer}</div>
        )}
      </div>
    </div>
  )
}
